import React, { useMemo, useRef, useState } from 'react';
import { Dimensions, FlatList, Platform, View } from 'react-native';
import addYears from 'date-fns/addYears';
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval';
import isAfter from 'date-fns/isAfter';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import isSameYear from 'date-fns/isSameYear';
import { getDates } from 'greg-cal';
import IcSystemChevronLeft from '@traveloka/district-icon/IcSystemChevronLeft';
import IcSystemChevronRight from '@traveloka/district-icon/IcSystemChevronRight';
import BMButtonIcon from '../ButtonIcon/BMButtonIcon';
import { SetDateProvider, LocaleProvider, SizeProvider, DateProvider, EndDateProvider, useToday, useHolidayConfig, DAY_COLUMNS, } from './context';
import Month from './Month';
import { useBMTheme } from '../../theme';
import useLocalization from './useLocalization';
/**
 * Calendar's entry point.
 * `date` props will define the usage of SingleCalendar or RangeCalendar
 */
function CalendarComponent(props) {
    if (Array.isArray(props.date)) {
        return <RangeCalendar {...props}/>;
    }
    return <SingleCalendar {...props}/>;
}
/**
 * RangeCalendar for range of dates use cases.
 * RangeCalendar internally uses `SingleCalendar` and additional `EndDateProvider` to handle range dates selection.
 */
function RangeCalendar(props) {
    const { date, onChange, ...restProps } = props;
    const [[startDate, endDate], setRangeDate] = useState(date);
    function handleChangeDate(newDate) {
        if (typeof startDate === 'undefined') {
            setRangeDate([newDate, undefined]);
            onChange([newDate, undefined]);
        }
        else {
            if (typeof endDate === 'undefined' && (isSameDay(newDate, startDate) || isAfter(newDate, startDate))) {
                setRangeDate([startDate, newDate]);
                onChange([startDate, newDate]);
            }
            else {
                setRangeDate([newDate, undefined]);
                onChange([newDate, undefined]);
            }
        }
    }
    return (<EndDateProvider value={endDate}>
      <SingleCalendar onChange={handleChangeDate} date={startDate} {...restProps}/>
    </EndDateProvider>);
}
/**
 * SingleCalendar component for single date use cases.
 */
function SingleCalendar(props) {
    // The width of calender is minimum 320px and maximum 560px
    const minWidth = 320;
    const maxWidth = Dimensions.get('window').width > 560 ? 560 : Dimensions.get('window').width;
    const today = useToday();
    const holidayConfig = useHolidayConfig();
    const { BMComponents, BMPrimitives } = useBMTheme();
    const { numberOfRenderedMonth = 1, autoScrollToSelectedDate = false, variant = 'horizontal', width = maxWidth, minDate: _minDate = today, maxDate: _maxDate = addYears(today, 1), date, onChange, colorConfig, hideArrows = false, todayLabel, hideHolidayBadges = false, hideLegendBadges = false, testID, } = props;
    /**
     * First day of the week, changes depends on locale
     * @default 'mon'
     * @see options include 'mon', 'sun'
     */
    const { firstDay, dayColor } = useLocalization();
    const horizontal = variant === 'horizontal';
    const flatList = useRef(null);
    const minDate = useRef(_minDate).current;
    const maxDate = useRef(_maxDate).current;
    const months = useRef(eachMonthOfInterval({
        start: minDate,
        end: maxDate,
    })).current;
    const isTwinCalendar = numberOfRenderedMonth === 2 && variant === 'horizontal';
    const [isPrevMonthDisabled, setIsPrevMonthDisabled] = useState(false);
    const [isNextMonthDisabled, setIsNextMonthDisabled] = useState(false);
    const isPrevMonthDisabledRef = useRef(isPrevMonthDisabled);
    const isNextMonthDisabledRef = useRef(isNextMonthDisabled);
    const parsedWidth = width < minWidth ? minWidth : width > maxWidth ? maxWidth : width;
    const selectedMonthIndex = useMemo(() => {
        let monthIndex = 0;
        if (date instanceof Date) {
            monthIndex = months.findIndex((m) => isSameYear(m, date) && isSameMonth(m, date));
        }
        return monthIndex !== -1 ? monthIndex : 0;
    }, [months, date]);
    const visibleMonthIndex = useRef(selectedMonthIndex);
    const setDate = (newDate) => {
        try {
            const displayedMonth = months[visibleMonthIndex.current].getMonth();
            const selectedMonth = newDate.getMonth();
            if (autoScrollToSelectedDate && selectedMonth > displayedMonth) {
                goToNextMonth();
            }
        }
        catch (e) {
            console.error('failed to parse date object: ', e);
        }
        onChange(newDate);
    };
    const goToPrevMonth = useRef(() => {
        if (flatList.current === null || visibleMonthIndex.current === 0)
            return;
        flatList.current.scrollToIndex({
            animated: true,
            index: visibleMonthIndex.current - 1,
        });
    }).current;
    const goToNextMonth = useRef(() => {
        const visibleCurrentMonth = visibleMonthIndex.current + 1;
        if (flatList.current === null || visibleCurrentMonth === months.length)
            return;
        flatList.current.scrollToIndex({
            animated: true,
            index: visibleMonthIndex.current + 1,
        });
    }).current;
    const renderItem = (BMComponents) => ({ item, index }) => {
        const defaultColorConfig = {
            selectedDateBackgroundColor: BMComponents.BMUiCalendarBgcolorSelected,
            selectedDateBorderColor: BMComponents.BMUiCalendarBordercolorSelected,
            selectedMidBackgroundColor: BMComponents.BMUiCalendarBgcolorSelected,
            ...colorConfig,
        };
        return (<Month year={item.getFullYear()} month={item.getMonth()} horizontal={horizontal} firstDay={firstDay} minDate={minDate} maxDate={maxDate} onPrevMonthPress={goToPrevMonth} onNextMonthPress={goToNextMonth} onLayout={({ nativeEvent }) => {
                if (!layoutHeightByIndex[index]) {
                    layoutHeightByIndex[index] = nativeEvent.layout.height;
                }
            }} hideArrows={hideArrows || isTwinCalendar} colorConfig={defaultColorConfig} todayLabel={todayLabel} hideHolidayBadges={hideHolidayBadges} hideLegendBadges={hideLegendBadges} testID={'monthCell'} dayColor={dayColor}/>);
    };
    const renderAbsoluteNavigation = () => {
        return (<View style={{
                position: 'absolute',
                zIndex: 10,
                width: parsedWidth * numberOfRenderedMonth,
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: BMPrimitives.BMSize.md,
                paddingVertical: BMPrimitives.BMSize.sm,
                justifyContent: 'space-between',
            }}>
        <BMButtonIcon Icon={IcSystemChevronLeft} onPress={goToPrevMonth} size={'INSET'} disabled={isPrevMonthDisabled}/>
        <BMButtonIcon Icon={IcSystemChevronRight} onPress={goToNextMonth} size={'INSET'} disabled={isNextMonthDisabled}/>
      </View>);
    };
    const layoutHeightByIndex = useRef({}).current;
    const getLayoutHeightForIndex = useRef((index) => {
        var _a;
        if (layoutHeightByIndex[index]) {
            return layoutHeightByIndex[index];
        }
        const monthDate = months[index];
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        // MonthCell
        const monthCellHeight = 36;
        // WeekBar
        const weekBarHeight = 24;
        // DateGroup
        const monthDates = getDates(year, month, firstDay);
        const dateGroupRows = Math.ceil(monthDates.length / DAY_COLUMNS);
        const dateCellHeight = parsedWidth / DAY_COLUMNS;
        const dateGroupHeight = dateGroupRows * dateCellHeight;
        // HolidayBar
        const holiday = (_a = holidayConfig === null || holidayConfig === void 0 ? void 0 : holidayConfig[year]) === null || _a === void 0 ? void 0 : _a[month + 1]; // holiday month starts from 1
        const holidayBarHeight = holiday ? 42 : 0;
        return monthCellHeight + weekBarHeight + dateGroupHeight + holidayBarHeight;
    }).current;
    const getLayoutOffsetForIndex = useRef((index) => {
        return months
            .slice(0, index)
            .map((_, i) => getLayoutHeightForIndex(i))
            .reduce((prev, curr) => prev + curr, 0);
    }).current;
    // define Month's item layout to support `scrollToIndex` and `initialScrollIndex`
    const getItemLayout = useRef((_, index) => {
        if (horizontal) {
            return {
                length: parsedWidth,
                offset: parsedWidth * index,
                index,
            };
        }
        // appeared on vertical FlatList with total item height > screenHeight :|
        if (index === -1) {
            return {
                length: 0,
                offset: 0,
                index,
            };
        }
        return {
            length: getLayoutHeightForIndex(index),
            offset: getLayoutOffsetForIndex(index),
            index,
        };
    }).current;
    // provide `onViewableItemsChanged` for handling `visibleMonthIndex` value, used in go to prev/next month method
    const viewabilityConfig = {
        itemVisiblePercentThreshold: 75,
    };
    const onViewableItemsChanged = useRef(({ viewableItems }) => {
        if (viewableItems.length > 0) {
            const [viewableItem] = viewableItems;
            if (viewableItem.isViewable && viewableItem.index !== null) {
                visibleMonthIndex.current = viewableItem.index;
            }
            setAbsoluteNavigationConfig(viewableItem.index, months.length);
        }
    }).current;
    function setAbsoluteNavigationConfig(currentIndex, maxIndex) {
        if (currentIndex === 0 && !isPrevMonthDisabledRef.current) {
            setIsPrevMonthDisabled(true);
            isPrevMonthDisabledRef.current = true;
        }
        else if (currentIndex > 0 && isPrevMonthDisabledRef.current) {
            setIsPrevMonthDisabled(false);
            isPrevMonthDisabledRef.current = false;
        }
        if (currentIndex === maxIndex - 2 && !isNextMonthDisabledRef.current) {
            setIsNextMonthDisabled(true);
            isNextMonthDisabledRef.current = true;
        }
        else if (currentIndex < maxIndex - 2 && isNextMonthDisabledRef.current) {
            setIsNextMonthDisabled(false);
            isNextMonthDisabledRef.current = false;
        }
    }
    return (<SizeProvider value={parsedWidth}>
      <DateProvider value={date}>
        <SetDateProvider value={setDate}>
          {isTwinCalendar && renderAbsoluteNavigation()}
          <FlatList ref={flatList} style={{
            width: parsedWidth * (variant === 'horizontal' ? numberOfRenderedMonth : 1),
            backgroundColor: BMComponents.BMUiCalendarBgcolorPrimary,
        }} initialScrollIndex={selectedMonthIndex} getItemLayout={getItemLayout} onViewableItemsChanged={onViewableItemsChanged} viewabilityConfig={viewabilityConfig} horizontal={horizontal} pagingEnabled={horizontal} showsHorizontalScrollIndicator={false} showsVerticalScrollIndicator={false} removeClippedSubviews={Platform.OS === 'android'} data={months} keyExtractor={(item) => `${item.getFullYear()}-${item.getMonth()}`} renderItem={renderItem(BMComponents)} testID={testID}/>
        </SetDateProvider>
      </DateProvider>
    </SizeProvider>);
}
const BMCalendar = (props) => {
    const { locale = 'en-en' } = props;
    return (<LocaleProvider value={locale.toLowerCase()}>
      <CalendarComponent {...props}/>
    </LocaleProvider>);
};
export default BMCalendar;
